import AiApi from "@/api/ai";
import { computed, onBeforeMount, readonly, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useExpiringSessionStorage } from "@/common/composables/expiring-session-storage";
import { useContext } from "@/store/context";
export const useAiIngestStore = defineStore("ai-ingest", () => {
    const { currentStudyId } = storeToRefs(useContext());
    const expiringSessionStorage = useExpiringSessionStorage();
    const pending = ref(false);
    // Using a ref instead of a computed ref because of timing issues due to usage of localStorage + JSON.parse/stringify
    const aiIngestStudyState = ref();
    const embeddingBackfillState = ref(undefined);
    // Content is fully ingested if this returns null
    const siteEmbeddingsAvailableSince = computed(() => {
        // Is the site fully ingested?
        if (!embeddingBackfillState.value || embeddingBackfillState.value?.fullyBackfilled) {
            return null;
        }
        // Otherwise use the date for the partial backfill if it's available
        return embeddingBackfillState.value?.backfillDate ?? null;
    });
    const noDataIngestedForStudy = computed(() => {
        return (!pending.value &&
            currentStudyId.value !== undefined &&
            aiIngestStudyState.value &&
            !aiIngestStudyState.value.hasIngestedData);
    });
    const asyncActivitiesOpenedAsOf = computed(() => {
        if (pending.value || !aiIngestStudyState.value?.asyncActivitiesIngestState?.ingestedDate) {
            return undefined;
        }
        return aiIngestStudyState.value.asyncActivitiesIngestState.ingestedDate;
    });
    const liveActivitiesOpenedAsOf = computed(() => {
        if (pending.value || !aiIngestStudyState.value?.liveActivitiesIngestState?.ingestedDate) {
            return undefined;
        }
        return aiIngestStudyState.value.liveActivitiesIngestState.ingestedDate;
    });
    const isActivityIngested = (activity) => {
        if (embeddingBackfillState.value && embeddingBackfillState.value?.backfillDate) {
            return (embeddingBackfillState.value.fullyBackfilled ||
                activity.dateCreated >= embeddingBackfillState.value.backfillDate);
        }
        return true;
    };
    const fetchAiIngestStudyState = async (studyId) => {
        // If no cached state OR cached state studyId does not match then fetch the state
        pending.value = true;
        const newState = {
            ...(await AiApi.fetchAiIngestStudyState(studyId)),
            timestamp: Date.now()
        };
        expiringSessionStorage.set(`aiIngestStudyState-${currentStudyId.value}`, newState, 30);
        aiIngestStudyState.value = newState;
        pending.value = false;
        return newState;
    };
    const fetchEmbeddingBackfillState = async () => {
        const cachedBackfillState = expiringSessionStorage.get("embeddingBackfillState");
        if (cachedBackfillState) {
            embeddingBackfillState.value = cachedBackfillState;
            return;
        }
        // Fetch the embedding backfill state from the API
        pending.value = true;
        AiApi.fetchEmbeddingBackfillState()
            .then((newState) => {
            embeddingBackfillState.value = newState;
            expiringSessionStorage.set("embeddingBackfillState", newState, 30);
        })
            .catch((error) => {
            console.error("Error fetching embedding backfill state:", error);
        })
            .finally(() => {
            pending.value = false;
        });
    };
    const getAiIngestState = async () => {
        if (!currentStudyId.value) {
            throw new Error('"fetchAiIngestState" cannot be called outside a study scope.');
        }
        // Retrieve from cache if needed
        if (!aiIngestStudyState.value) {
            aiIngestStudyState.value =
                expiringSessionStorage.get(`aiIngestStudyState-${currentStudyId.value}`) ?? undefined;
        }
        if (!aiIngestStudyState.value) {
            return fetchAiIngestStudyState(currentStudyId.value);
        }
        return aiIngestStudyState.value;
    };
    onBeforeMount(() => {
        if (pending.value)
            return;
        fetchEmbeddingBackfillState();
        if (currentStudyId.value) {
            getAiIngestState();
        }
    });
    const sendStudyDataForAiProcessing = async (requestDto) => {
        return AiApi.postStudyDataForAiProcessing(requestDto);
    };
    return {
        getAiIngestState,
        sendStudyDataForAiProcessing,
        isActivityIngested,
        aiIngestRequestPending: readonly(pending),
        aiIngestStudyState,
        noDataIngested: noDataIngestedForStudy,
        embeddingBackfillState,
        asyncActivitiesOpenedAsOf,
        liveActivitiesOpenedAsOf,
        siteEmbeddingsAvailableSince
    };
});
